'use client';

import * as React from 'react';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getValidTimeForDate } from '~/core/generic/utils/timePickerUtils';
import Button from '~/core/ui/Button';
import { format } from 'date-fns';
import CalendarIcon from 'public/assets/images/svgs/CalendarIcon';
import { DateRange, Matcher, TZDate } from 'react-day-picker';

import { cn } from '../generic/shadcn-utils';
import {
  canDepartOnDate,
  canReturnOnDate,
  type DayAvailability,
  type SpecialDateRule,
} from '../generic/utils/storeAvailability';
import {
  dayBufferState,
  flipRole,
  inTurnaroundBufferBand,
  isLegalAnchor,
  isLegalEndGivenAnchor,
  toStrictBookings,
  toTurnaroundBookings,
  type TurnaroundBookingInput,
} from '../generic/utils/turnaround-availability';
import {
  convertUtcToOrgTimezone,
  createCheckInDateTime,
  createCheckOutDateTime,
} from '../generic/utils/timezoneUtils';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

export interface DateRangePickerProps {
  defaultDates?: {
    from?: string | null;
    to?: string | null;
  };
  className?: string;

  onChange?: (range: { from: string | null; to: string | null }) => void;
  side?: 'top' | 'right' | 'bottom' | 'left';

  hideYear?: boolean;
  fontSize?: string; // Tailwind class name e.g. 'text-sm', 'text-lg'
  fontWeight?: string; // Tailwind class name e.g. 'font-medium', 'font-bold'
  iconColor?: string; // Tailwind class name e.g. 'text-blue-500'
  customTrigger?: ReactNode;
  disablePastDates?: boolean;
  earliestSelectableDate?: string; // UTC ISO string - overrides disablePastDates for edit mode
  disabledDates?: string[];
  // Range-relative availability (Days Between Reservations). When provided,
  // supersedes the flat `disabledDates` list: each booking blocks its span
  // (plus its buffer tail), the picker recomputes legal end dates after the
  // first click, and buffer-0 multi-day bookings keep their endpoints
  // selectable as half-day "flips" (rendered with a diagonal split). Callers
  // resolve each booking's buffer via buildTurnaroundBookings or pass it
  // directly.
  turnaroundBookings?: TurnaroundBookingInput[];
  // Buffer governing the reservation being formed (the new reservation's own
  // turnaround after its return). Constrains how close its return may sit
  // before a later booking's departure; 0 allows returning on a booking's
  // departure day (flip). Callers should re-resolve this when the selected
  // departure date changes (onChange fires on partial selection).
  newReservationBufferDays?: number;
  // Soft Days Between Reservations enforcement (operator UI only). When true,
  // days that violate ONLY a turnaround buffer (not a physical overlap) stay
  // SELECTABLE and are marked with a hatched band instead of being disabled:
  // the reveal-but-mark model from the spec. Physical overlaps and flip rules
  // remain hard. Defaults to false, so the Customer Interface keeps its hard
  // enforcement untouched.
  softBuffer?: boolean;
  // Inside a modal dialog the dialog's scroll lock cancels wheel and touch
  // scrolling in this body-portaled popover; a modal popover owns the lock
  // while open (QA-1120).
  modal?: boolean;
  autoClose?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  numberOfMonths?: number; // Number of months to display in the calendar
  isLoading?: boolean; // Show loading spinner instead of calendar icon
  disabled?: boolean;
  // Organization settings for complete UTC conversion
  organizationTimezone: string;
  defaultPickupTime?: string; // e.g., "16:00" - for checkout/departure dates (optional, defaults to noon)
  defaultDropoffTime?: string; // e.g., "11:00" - for checkin/return dates (optional, defaults to noon)
  // Weekday indices (0=Sun..6=Sat) the customer may NOT pick, applied
  // phase-aware: the departure set while choosing the start of the range, the
  // return set while choosing the end. Used by the CI to enforce store-day
  // availability (departure only on Open days, return on Open/Returns-only days).
  disabledDepartureWeekdays?: number[];
  disabledReturnWeekdays?: number[];
  // Store's seven day-of-week modes (index 0=Sun..6=Sat). When provided, the
  // picker resolves each cell's effective availability (applying the
  // `specialDates` override on top of this weekly pattern) instead of the
  // precomputed disabled-weekday arrays above. Callers that don't pass this fall
  // back to the weekday-array path, so existing behavior is unchanged.
  weekAvailability?: DayAvailability[];
  // Per-calendar-date overrides (holidays). Resolved per visible cell (recurrence
  // is unbounded, so these can't be pre-expanded into disabledDates).
  specialDates?: SpecialDateRule[];
  // Persistent note rendered in the footer (e.g. explaining why days are greyed).
  availabilityNote?: ReactNode;
}

// Build a YYYY-MM-DD key from a date's timezone-aware Y/M/D components. Callers
// pass dates already expressed in the organization timezone, so the key names the
// calendar day directly and matches the disabled-date set built above.
const toDayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// Occupied-half shading for flip-day cells (slate-400 @ 35%), visible on both
// light and dark calendar backgrounds.
const FLIP_SHADE = 'rgba(148, 163, 184, 0.35)';

// Hatched amber band for buffer-tail days in soft (operator) mode: selectable,
// but visibly marked as a Days Between Reservations conflict. Diagonal repeating
// stripes distinguish it from the solid disabled / flip shading.
const BUFFER_HATCH =
  'repeating-linear-gradient(135deg, rgba(245, 158, 11, 0.18) 0, rgba(245, 158, 11, 0.18) 4px, transparent 4px, transparent 8px)';

// Helper function to compare dates by date only (ignoring time)
const isSameOrAfterDate = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() > date2.getFullYear() ||
    (date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() > date2.getMonth()) ||
    (date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() >= date2.getDate())
  );
};

export function DateRangePicker({
  className,
  defaultDates,
  onChange,
  side,
  hideYear,
  fontSize,
  fontWeight,
  iconColor = 'text-blue-500',
  customTrigger,
  organizationTimezone,
  disablePastDates,
  earliestSelectableDate,
  disabledDates = [],
  turnaroundBookings,
  newReservationBufferDays = 0,
  softBuffer = false,
  open,
  autoClose = true,
  onOpenChange,
  numberOfMonths = 2,
  isLoading = false,
  disabled = false,
  defaultPickupTime,
  defaultDropoffTime,
  disabledDepartureWeekdays = [],
  disabledReturnWeekdays = [],
  weekAvailability,
  specialDates,
  availabilityNote,
  modal = false,
}: DateRangePickerProps) {

  const today = useMemo(
    () => new TZDate(new Date(), organizationTimezone),
    [organizationTimezone],
  );

  // Two months never fit a phone viewport; the popover ends up wider than the
  // screen and Radix can't collision-shift it back into view. Below the sm
  // breakpoint always render a single month, regardless of numberOfMonths.
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsNarrowViewport(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const convertUtcStringToOrgTimezoneDate = useCallback(
    (utcString: string): TZDate => {
      // Use the proper timezone utility to convert UTC to organization timezone
      const converted = convertUtcToOrgTimezone(
        utcString,
        organizationTimezone,
      );

      const [year, month, day] = converted.dateOnly.split('-').map(Number);
      const orgTimezoneDate = new TZDate(
        year,
        month - 1,
        day,
        12,
        0,
        0,
        0,
        organizationTimezone,
      );

      return orgTimezoneDate;
    },
    [organizationTimezone],
  );

  const earliestDate = useMemo(() => {
    if (earliestSelectableDate) {
      return convertUtcStringToOrgTimezoneDate(earliestSelectableDate);
    }
    return today;
  }, [earliestSelectableDate, today, convertUtcStringToOrgTimezoneDate]);

  const [remountCounter, setRemountCounter] = useState(0);

  const initialMonth = defaultDates?.from
    ? new TZDate(new Date(defaultDates.from), organizationTimezone)
    : new TZDate(new Date(), organizationTimezone);

  const [currentMonth, setCurrentMonth] = useState<TZDate>(initialMonth);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (defaultDates?.from) {
      const newMonth = new TZDate(
        new Date(defaultDates.from),
        organizationTimezone,
      );
      setCurrentMonth(newMonth);
    }
  }, [defaultDates?.from, organizationTimezone]);

  const handleMonthChange = (month: Date) => {
    const tzMonth = new TZDate(month, organizationTimezone);
    setCurrentMonth(tzMonth);
  };

  const [selectedRange, setSelectedRange] = useState<{
    from?: Date;
    to?: Date;
  }>(() => {
    // Initialize with defaultDates if available to prevent flashing
    if (defaultDates?.from && defaultDates?.to) {
      // Handle both UTC strings and date-only strings
      const fromDate =
        defaultDates.from.includes('T') || defaultDates.from.endsWith('Z')
          ? convertUtcStringToOrgTimezoneDate(defaultDates.from)
          : new TZDate(
              new Date(`${defaultDates.from}T12:00:00`),
              organizationTimezone,
            );

      const toDate =
        defaultDates.to.includes('T') || defaultDates.to.endsWith('Z')
          ? convertUtcStringToOrgTimezoneDate(defaultDates.to)
          : new TZDate(
              new Date(`${defaultDates.to}T12:00:00`),
              organizationTimezone,
            );

      return {
        from: fromDate,
        to: toDate,
      };
    }
    return {};
  });

  // Use a ref to track if we've initialized from defaultDates
  const initializedRef = useRef(false);

  // Initialize selectedRange with defaultDates when they become available
  // but only once and only if defaultDates has valid dates
  useEffect(() => {
    // Skip if we've already initialized or if defaultDates is not valid
    if (initializedRef.current || !defaultDates || !defaultDates.from) {
      return;
    }

    const fromDate = defaultDates.from
      ? convertUtcStringToOrgTimezoneDate(defaultDates.from)
      : undefined;
    const toDate = defaultDates.to
      ? convertUtcStringToOrgTimezoneDate(defaultDates.to)
      : undefined;

    if (fromDate) {
      setSelectedRange({
        from: fromDate,
        to: toDate,
      });
      // Mark as initialized so we don't set it again
      initializedRef.current = true;
    }
  }, [defaultDates, organizationTimezone, convertUtcStringToOrgTimezoneDate]);

  // Normalized set of disabled day keys (YYYY-MM-DD in the org timezone) for O(1)
  // lookup. The disabled-dates payload is a flat list of UTC timestamps (one per
  // blocked day) that grows with how heavily an RV is reserved, so every consumer
  // below reads from this set instead of re-scanning/re-parsing the whole array.
  const disabledDateKeySet = useMemo(() => {
    const set = new Set<string>();
    for (const input of disabledDates) {
      if (!input) continue;
      try {
        // Same instant->day normalization the calendar uses to grey out cells,
        // so the range check stays consistent with what is visually disabled.
        const { dateOnly } = convertUtcToOrgTimezone(input, organizationTimezone);
        if (dateOnly) set.add(dateOnly);
      } catch (error) {
        console.error('[DateRangePicker] Error parsing date input:', error);
      }
    }
    return set;
  }, [disabledDates, organizationTimezone]);

  // O(1) matcher react-day-picker calls per rendered cell. Previously the disabled
  // prop was an N-element array, so RDP did O(cells x disabledDates) work on every
  // render/remount, the dominant per-click cost on heavily-reserved RVs.
  const isExplicitlyDisabled = useCallback(
    (date: Date) =>
      disabledDateKeySet.has(toDayKey(new TZDate(date, organizationTimezone))),
    [disabledDateKeySet, organizationTimezone],
  );

  // Range-relative availability mode (Days Between Reservations). Active when
  // the caller supplies booking spans; the flat disabledDates list is ignored
  // and legality is computed against each booking's blocked span.
  const turnaroundMode = Array.isArray(turnaroundBookings);

  const normalizedBookings = useMemo(
    () => toTurnaroundBookings(turnaroundBookings, organizationTimezone),
    [turnaroundBookings, organizationTimezone],
  );

  // Soft enforcement (operator UI): the hard matcher disables only physical
  // overlaps (buffers stripped to 0), so buffer-tail days stay selectable and
  // are marked instead (see isBufferWarn). The CI leaves softBuffer false, so
  // its hard matcher keeps the full buffered model. The candidate's own buffer
  // is likewise dropped from the hard test under softBuffer.
  const hardBookings = useMemo(
    () => (softBuffer ? toStrictBookings(normalizedBookings) : normalizedBookings),
    [softBuffer, normalizedBookings],
  );
  const hardNewBuffer = softBuffer ? 0 : newReservationBufferDays;

  // Which endpoint the next click selects. With a start chosen but no end yet,
  // the calendar is choosing the RETURN; otherwise (empty or a complete range)
  // the next click starts a new range, i.e. the DEPARTURE. The calendar remounts
  // on every click (see remountCounter key), so this recomputes and the matcher
  // below flips: Returns-only days go from greyed (departure) to selectable
  // (return) once a departure is chosen.
  const isSelectingReturn = !!selectedRange.from && !selectedRange.to;

  const anchorDayKey =
    isSelectingReturn && selectedRange.from
      ? toDayKey(new TZDate(selectedRange.from, organizationTimezone))
      : null;

  // Phase-aware turnaround matcher: the recompute-after-first-click model.
  // Anchor phase: any day not inside a booking's blocked span is a legal
  // start. Return phase: a day on/after the anchor is legal only if the
  // formed range stays clear of every blocked span (flip touches excepted);
  // a day before the anchor would restart the range, so it is validated as
  // an anchor again. The calendar remounts per click, so this recomputes.
  const isTurnaroundBlocked = useCallback(
    (date: Date): boolean => {
      if (!turnaroundMode) return false;
      const key = toDayKey(new TZDate(date, organizationTimezone));
      if (anchorDayKey && key >= anchorDayKey) {
        return !isLegalEndGivenAnchor(
          anchorDayKey,
          key,
          hardBookings,
          hardNewBuffer,
        );
      }
      return !isLegalAnchor(key, hardBookings);
    },
    [
      turnaroundMode,
      anchorDayKey,
      hardBookings,
      hardNewBuffer,
      organizationTimezone,
    ],
  );

  // Soft mode only: a day that clears the physical floor but violates a
  // turnaround buffer: selectable, but hatched and warned. Returns false in
  // hard mode (the CI), where such days are already disabled above.
  const isBufferWarn = useCallback(
    (date: Date): boolean => {
      if (!turnaroundMode || !softBuffer) return false;
      const key = toDayKey(new TZDate(date, organizationTimezone));
      // Static band identical to the Calendar page (drawGridBufferBand): hatch
      // both ends of every booking, INCLUDING its departure/return endpoint
      // cells, so the band wraps the reservation exactly as the grid does.
      if (inTurnaroundBufferBand(key, normalizedBookings)) return true;
      // Plus the candidate-relative soft-buffer days for the endpoint currently
      // being chosen (return-phase look-ahead toward an upcoming booking).
      return (
        dayBufferState(
          key,
          normalizedBookings,
          newReservationBufferDays,
          anchorDayKey,
        ) === 'buffer'
      );
    },
    [
      turnaroundMode,
      softBuffer,
      normalizedBookings,
      newReservationBufferDays,
      anchorDayKey,
      organizationTimezone,
    ],
  );

  // Phase-aware store-availability matcher: disable the days invalid for the
  // endpoint currently being selected. When `weekAvailability` is supplied, each
  // cell is resolved per calendar date so a special-date override wins over the
  // weekly pattern (e.g. an `open` holiday re-enables a normally-closed Sunday).
  // Otherwise it falls back to the precomputed disabled-weekday arrays. Day-key
  // and weekday are computed in the org timezone to match toDayKey/cell render.
  const isStoreUnavailable = useCallback(
    (date: Date): boolean => {
      const tz = new TZDate(date, organizationTimezone);
      const dow = tz.getDay();

      if (weekAvailability) {
        const dayKey = toDayKey(tz);
        return isSelectingReturn
          ? !canReturnOnDate(weekAvailability, dow, specialDates, dayKey)
          : !canDepartOnDate(weekAvailability, dow, specialDates, dayKey);
      }

      const set = isSelectingReturn
        ? disabledReturnWeekdays
        : disabledDepartureWeekdays;
      if (!set || set.length === 0) return false;
      return set.includes(dow);
    },
    [
      isSelectingReturn,
      weekAvailability,
      specialDates,
      disabledReturnWeekdays,
      disabledDepartureWeekdays,
      organizationTimezone,
    ],
  );

  // Disabled-day matchers for the calendar: the past/earliest-date window
  // (unchanged), the reservation-availability rule (range-relative turnaround
  // spans when provided, else the explicitly-disabled flat set), and the
  // phase-aware store rule.
  const disabledMatchers = useMemo(() => {
    const matchers: Matcher[] = [];

    // Priority: earliestSelectableDate (edit mode) > today (if disablePastDates).
    if (earliestSelectableDate) {
      matchers.push({ before: earliestDate });
    } else if (disablePastDates) {
      matchers.push({ before: today });
    }

    matchers.push(turnaroundMode ? isTurnaroundBlocked : isExplicitlyDisabled);
    matchers.push(isStoreUnavailable);

    return matchers;
  }, [
    disablePastDates,
    earliestDate,
    earliestSelectableDate,
    today,
    turnaroundMode,
    isTurnaroundBlocked,
    isExplicitlyDisabled,
    isStoreUnavailable,
  ]);

  // Flip-day rendering (turnaround mode): a buffer-0 multi-day booking's
  // endpoints are shareable half-days, conveyed with a diagonal split; the
  // shaded half is the part of the day the unit is occupied. A return day is
  // shaded in the morning (unit not back yet; a new rental may DEPART that
  // day); a departure day is shaded in the afternoon (a new rental may
  // RETURN that day). Suppressed on cells that are disabled in the current
  // phase or part of the current selection, so disabled/selected styling
  // stays authoritative.
  const getFlipKind = useCallback(
    (date: Date): 'dep' | 'ret' | 'both' | null => {
      if (!turnaroundMode || normalizedBookings.length === 0) return null;
      const tz = new TZDate(date, organizationTimezone);
      const key = toDayKey(tz);

      const role = flipRole(key, normalizedBookings, newReservationBufferDays);
      if (!role.asStart && !role.asEnd) return null;

      // Hide the split on cells the current phase disables (occupied spans,
      // store-closed days, past days); they render as plain disabled.
      if (isTurnaroundBlocked(date) || isStoreUnavailable(date)) return null;
      if (earliestSelectableDate) {
        if (!isSameOrAfterDate(tz, earliestDate)) return null;
      } else if (disablePastDates && !isSameOrAfterDate(tz, today)) {
        return null;
      }

      // Selection styling wins over the split on the chosen endpoints and the
      // in-range fill.
      const fromKey = selectedRange.from
        ? toDayKey(new TZDate(selectedRange.from, organizationTimezone))
        : null;
      const toKeyStr = selectedRange.to
        ? toDayKey(new TZDate(selectedRange.to, organizationTimezone))
        : null;
      if (fromKey && key === fromKey) return null;
      if (toKeyStr && key === toKeyStr) return null;
      if (fromKey && toKeyStr && key > fromKey && key < toKeyStr) return null;

      if (role.asStart && role.asEnd) return 'both';
      return role.asStart ? 'ret' : 'dep';
    },
    [
      turnaroundMode,
      normalizedBookings,
      newReservationBufferDays,
      organizationTimezone,
      isTurnaroundBlocked,
      isStoreUnavailable,
      earliestSelectableDate,
      earliestDate,
      disablePastDates,
      today,
      selectedRange.from,
      selectedRange.to,
    ],
  );

  const hasFlipDays = useMemo(
    () =>
      turnaroundMode &&
      normalizedBookings.some(
        (b) => b.bufferDays === 0 && b.depKey !== b.retKey,
      ),
    [turnaroundMode, normalizedBookings],
  );

  const [dateInfo, setDateInfo] = useState<{
    from: { date: string; iso: string } | null;
    to: { date: string; iso: string } | null;
  } | null>(null);

  // True if any day in [from, to] is disabled. Walks the range once with an O(1)
  // set lookup per day; previously this was O(daysInRange x disabledDates) with a
  // fresh Date/TZDate parse on every pair, the main cost when closing a range on a
  // busy RV.
  const hasDisabledDatesInRange = useCallback(
    (from: Date, to: Date): boolean => {
      if (disabledDates.length === 0) {
        return false;
      }

      const current = new TZDate(from, organizationTimezone);
      const toTZ = new TZDate(to, organizationTimezone);

      while (current <= toTZ) {
        if (disabledDateKeySet.has(toDayKey(current))) {
          return true;
        }
        current.setDate(current.getDate() + 1);
      }

      return false;
    },
    [disabledDates, disabledDateKeySet, organizationTimezone],
  );

  const handleDayClick = (day: Date) => {
    const tzDate = new TZDate(day, organizationTimezone);

    setRemountCounter((prev) => prev + 1);

    const prev = { ...selectedRange };
    let newRange = { ...selectedRange };

    const isBeforeEarliest = earliestSelectableDate
      ? !isSameOrAfterDate(tzDate, earliestDate)
      : disablePastDates
        ? !isSameOrAfterDate(tzDate, today)
        : false;

    // Determine the role this click will play using the SAME branch logic as the
    // range update below: a click lands as the RETURN only when a start exists,
    // no end is set, and the click is on/after the start; otherwise it (re)starts
    // the range as the DEPARTURE. Validate the weekday against that role so a
    // Returns-only day can't sneak in as a departure via the reset-before-start
    // path (it would be enabled in the return phase but is invalid as a start).
    const willBeReturn = !!prev.from && !prev.to && !(tzDate < prev.from);
    const dow = tzDate.getDay();
    let isStoreBlocked: boolean;
    if (weekAvailability) {
      const dayKey = toDayKey(tzDate);
      isStoreBlocked = willBeReturn
        ? !canReturnOnDate(weekAvailability, dow, specialDates, dayKey)
        : !canDepartOnDate(weekAvailability, dow, specialDates, dayKey);
    } else {
      const weekdaySet = willBeReturn
        ? disabledReturnWeekdays
        : disabledDepartureWeekdays;
      isStoreBlocked = weekdaySet?.includes(dow) ?? false;
    }

    // Reservation-availability validation for the role this click plays:
    // range-relative spans in turnaround mode, flat disabled set otherwise.
    const clickedDayKey = toDayKey(tzDate);
    const prevFromKey = prev.from
      ? toDayKey(new TZDate(prev.from, organizationTimezone))
      : null;
    const isReservationBlocked = turnaroundMode
      ? willBeReturn && prevFromKey
        ? !isLegalEndGivenAnchor(
            prevFromKey,
            clickedDayKey,
            hardBookings,
            hardNewBuffer,
          )
        : !isLegalAnchor(clickedDayKey, hardBookings)
      : disabledDateKeySet.has(clickedDayKey);

    const isDisabledDay = isBeforeEarliest || isReservationBlocked || isStoreBlocked;

    // If the day is disabled, don't proceed with selection
    if (isDisabledDay) {
      return;
    }

    // Implement the desired behavior J asked for
    if (prev?.to || (prev?.from && tzDate < prev.from)) {
      newRange = { from: tzDate, to: undefined };
    } else if (prev?.from) {
      // Turnaround mode already validated the formed range via
      // isLegalEndGivenAnchor above; the flat-list path still walks the range
      // for disabled islands the per-day matcher can't see.
      if (!turnaroundMode && hasDisabledDatesInRange(prev.from, tzDate)) {
        newRange = { from: tzDate, to: undefined };
      } else {
        newRange = { from: prev.from, to: tzDate };
        if (autoClose) {
          setIsOpen(false);
        }
      }
    } else {
      newRange = { from: tzDate, to: undefined };
    }

    setSelectedRange(newRange);

    setDateInfo({
      from: newRange.from
        ? {
            date: newRange.from.toString(),
            iso: newRange.from.toISOString(),
          }
        : null,
      to: newRange.to
        ? {
            date: newRange.to.toString(),
            iso: newRange.to.toISOString(),
          }
        : null,
    });

    if (onChange) {
      if (newRange.from && newRange.to) {
        const fromDateString = `${newRange.from.getFullYear()}-${String(newRange.from.getMonth() + 1).padStart(2, '0')}-${String(newRange.from.getDate()).padStart(2, '0')}`;
        const toDateString = `${newRange.to.getFullYear()}-${String(newRange.to.getMonth() + 1).padStart(2, '0')}-${String(newRange.to.getDate()).padStart(2, '0')}`;

        const validPickupTime = getValidTimeForDate(
          fromDateString,
          defaultPickupTime || '12:00',
          organizationTimezone,
        );

        const validDropoffTime = getValidTimeForDate(
          toDateString,
          defaultDropoffTime || '12:00',
          organizationTimezone,
        );

        const checkOutUtc = createCheckOutDateTime(
          fromDateString,
          validPickupTime,
          organizationTimezone,
        ).utcIsoString;

        const checkInUtc = createCheckInDateTime(
          toDateString,
          validDropoffTime,
          organizationTimezone,
        ).utcIsoString;

        onChange({
          from: checkOutUtc,
          to: checkInUtc,
        });
      } else {
        let fromUtc = null;
        let toUtc = null;

        if (newRange.from) {
          const fromDateString = `${newRange.from.getFullYear()}-${String(newRange.from.getMonth() + 1).padStart(2, '0')}-${String(newRange.from.getDate()).padStart(2, '0')}`;

          const validPickupTime = getValidTimeForDate(
            fromDateString,
            defaultPickupTime || '12:00',
            organizationTimezone,
          );

          fromUtc = createCheckOutDateTime(
            fromDateString,
            validPickupTime,
            organizationTimezone,
          ).utcIsoString;
        }

        if (newRange.to) {
          const toDateString = `${newRange.to.getFullYear()}-${String(newRange.to.getMonth() + 1).padStart(2, '0')}-${String(newRange.to.getDate()).padStart(2, '0')}`;

          const validDropoffTime = getValidTimeForDate(
            toDateString,
            defaultDropoffTime || '12:00',
            organizationTimezone,
          );

          toUtc = createCheckInDateTime(
            toDateString,
            validDropoffTime,
            organizationTimezone,
          ).utcIsoString;
        }

        onChange({
          from: fromUtc,
          to: toUtc,
        });
      }
    }
  };

  const formatDate = (date: Date) => {
    return format(date, hideYear ? 'LLL dd' : 'LLL dd, y');
  };
  return (
    <div className={cn('grid gap-2 ', className)}>
      <Popover
        modal={modal}
        open={disabled ? false : isOpen}
        onOpenChange={(value) => {
          if (!disabled) {
            setIsOpen(value);
            onOpenChange?.(value);
          }
        }}
      >
        <PopoverTrigger asChild>
          {customTrigger || (
            <Button
              id="date"
              variant={'outline'}
              disabled={disabled}
              className={cn(
                'w-full special-case-dates relative ', // Make relative for absolute positioning
                !selectedRange && 'text-muted-foreground',
              )}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
            >
              <div className={cn('w-full text-center', fontSize)}>
                {selectedRange?.from ? (
                  selectedRange.to ? (
                    <>
                      {formatDate(selectedRange.from)} -{' '}
                      {formatDate(selectedRange.to)}
                    </>
                  ) : (
                    formatDate(selectedRange.from)
                  )
                ) : (
                  <span
                    className={cn(
                      'whitespace-nowrap',
                      fontSize ? fontSize : 'text-[10px] sm:text-xs lg:text-sm',
                      fontWeight,
                    )}
                  >
                    Choose Dates
                  </span>
                )}
              </div>

              {/* Position the calendar icon or loading spinner absolutely on the right */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {isLoading ? (
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full" />
                ) : (
                  <CalendarIcon className={cn('h-4 w-4', iconColor)} />
                )}
              </div>
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-auto max-w-[calc(100vw-16px)] p-0 overflow-y-auto max-h-[min(80vh,600px)]"
          align="center"
          side={side}
          sideOffset={5}
          collisionPadding={8}
        >
          <Calendar
            key={`calendar-${remountCounter}`}
            mode="range"
            numberOfMonths={isNarrowViewport ? 1 : numberOfMonths}
            timeZone={organizationTimezone}
            onDayClick={handleDayClick}
            defaultMonth={currentMonth}
            onMonthChange={handleMonthChange}
            disabled={disabledMatchers}
            modifiers={{
              disabled: disabledMatchers,
              flipDep: (date: Date) => getFlipKind(date) === 'dep',
              flipRet: (date: Date) => getFlipKind(date) === 'ret',
              flipBoth: (date: Date) => getFlipKind(date) === 'both',
              bufferWarn: isBufferWarn,
            }}
            modifiersStyles={{
              disabled: {
                textDecoration: 'line-through',
                color: '#999999',
              },
              // Diagonal split on shareable flip days; the shaded half is the
              // part of the day the unit is occupied. rgba shading keeps both
              // light and dark themes readable (inline styles can't theme).
              flipDep: {
                background: `linear-gradient(135deg, transparent 0%, transparent 50%, ${FLIP_SHADE} 50%, ${FLIP_SHADE} 100%)`,
              },
              flipRet: {
                background: `linear-gradient(135deg, ${FLIP_SHADE} 0%, ${FLIP_SHADE} 50%, transparent 50%, transparent 100%)`,
              },
              flipBoth: {
                background: `linear-gradient(135deg, ${FLIP_SHADE} 0%, ${FLIP_SHADE} 38%, transparent 38%, transparent 62%, ${FLIP_SHADE} 62%, ${FLIP_SHADE} 100%)`,
              },
              // Soft-mode buffer tail: hatched amber, still selectable. Use
              // background-IMAGE (not the `background` shorthand) so the hatch
              // layers OVER the cell's existing background-color instead of
              // resetting it, so a selected endpoint/in-range day that also sits
              // in a buffer keeps its blue selection fill AND shows the stripes.
              bufferWarn: {
                backgroundImage: BUFFER_HATCH,
              },
            }}
            initialFocus
            selected={selectedRange as DateRange | undefined}
          />
          {hasFlipDays && (
            <div className="px-3 pt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-3 rounded-sm border border-gray-300 dark:border-gray-600"
                  style={{
                    background: `linear-gradient(135deg, ${FLIP_SHADE} 0%, ${FLIP_SHADE} 50%, transparent 50%, transparent 100%)`,
                  }}
                />
                Available for departure
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-3 rounded-sm border border-gray-300 dark:border-gray-600"
                  style={{
                    background: `linear-gradient(135deg, transparent 0%, transparent 50%, ${FLIP_SHADE} 50%, ${FLIP_SHADE} 100%)`,
                  }}
                />
                Available for return
              </span>
            </div>
          )}
          {availabilityNote && (
            <div className="px-3 pt-2 text-xs text-gray-600">
              {availabilityNote}
            </div>
          )}
          <div className="p-2 flex justify-between items-center text-sm text-gray-500">
            <div>Timezone: {organizationTimezone}</div>
            {(selectedRange.from || selectedRange.to) && (
              <button
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRemountCounter((prev) => prev + 1);
                  setSelectedRange({});

                  setDateInfo(null);

                  if (onChange) {
                    onChange({
                      from: null,
                      to: null,
                    });
                  }
                }}
                className="text-[#3c83f6] hover:bg-accent px-2 py-1 text-base rounded transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
